import './ClaimableBalanceDetailsPage.css';
import { IonButtons, IonContent, IonButton, IonHeader, IonLabel, IonToolbar, IonIcon, useIonAlert, IonItem, IonText, IonCard, IonSpinner, IonNote, IonThumbnail, IonModal, useIonLoading } from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import moment from "moment";
import * as stellarApi from '../../services/stellar/horizon/data/data';
import * as stellarSdk from '@stellar/stellar-sdk';
import * as txBuilder from '../../services/stellar/TransactionBuilder';
import * as sep7 from '../../services/stellar/sep7';

import * as orunPayApi from '../../services/api/ebioro/ebioro';
import { Asset, DisplayableOperation, DelegateSigningParams, CollectionParams } from "../../types/types";
import AppHeader from '../../components/AppHeader/AppHeader';
import * as stellarUtils from '../../services/stellar/stellar-utils';
import ClaimableBalanceSuccessPage from './ClaimableBalanceSuccess';
import { useAppDispatch, useAppSelector } from '../../services/redux/hook';
import { ebioroApi, useDelegateSigningMutation } from "../../services/api/ebioro/ebioro";
import * as Sentry from "@sentry/react";
import { useHistory } from 'react-router';
import { App, BackButtonListenerEvent } from '@capacitor/app';

type MyModalProps = {

    operation: DisplayableOperation;
    closeAction: Function;
    asset: Asset;
}

const ClaimableBalancePage: React.FC<MyModalProps> = (props) => {


    // dealing with android back button and modals
    // App.addListener('backButton', (ev: BackButtonListenerEvent) => {
    //     props.closeAction()
    // });

    useEffect(() => {
        let backButtonListener:any;
    
        const setupBackButtonListener = async () => {
            backButtonListener = await App.addListener('backButton', (ev: BackButtonListenerEvent) => {
                props.closeAction();
            });
        };
    
        setupBackButtonListener();
    
        // Cleanup function
        return () => {
            backButtonListener?.remove();
        };
    }, [props.closeAction]);

    const [assetCode, setAssetCode] = useState('');
    const dispatch = useAppDispatch();
    const selectedTab = useAppSelector((state) => state.selectedTab)
    const { t, i18n } = useTranslation();
    const [isSpinner, setIsSpinner] = useState(false);
    const [claimSupported, setClaimSupported] = useState(true);
    const walletInfo = useAppSelector((state) => state.walletInfo);
    const [claimExist, setClaimExist] = useState(true);
    const [present] = useIonAlert();
    const [showClaimableBalanceSuccessPage, setShowClaimableBalanceSuccess] = useState(false);
    const [delegateSigning, result] = useDelegateSigningMutation();
    const [presentLoader, dismissLoader] = useIonLoading();
    const history = useHistory();

    const now = moment();


    const presentErrorAlert = (error: any) => {

        present({
            header: 'Error',
            message: `${error.data}`,
            buttons: [
                {
                    text: 'Close',
                    role: 'cancel',
                    handler: () => {
                        return
                    }
                }
            ],

        })

    }

    const goToClaimableBalanceSuccess = async () => {

        const params: CollectionParams = {}

        await dispatch(ebioroApi.endpoints.getOperations.initiate(params, { forceRefetch: true }));

        dismissLoader();

        setShowClaimableBalanceSuccess(true);
    }

    const closeClaimableBalanceSuccess = () => {

        if (selectedTab === 'send') {
            history.push('/tabs/payments');
        }

        else {
            history.push('/tabs/accounts');
        }

        setClaimSupported(false);
        props.closeAction()
        setShowClaimableBalanceSuccess(false);


    }


    const claimBalance = async (claimablePayment: DisplayableOperation) => {

        try {

            if (walletInfo.has_key) {

                const claimable_balance_id = claimablePayment.claimable_balance_id;
                let stellarTx: stellarSdk.Transaction<stellarSdk.Memo<stellarSdk.MemoType>, stellarSdk.Operation[]> | null;

                if (claimable_balance_id && claimablePayment.token.type === 'native') {

                    const [error, transaction] = await txBuilder.claimBalance(claimable_balance_id, walletInfo.account_key as string, walletInfo.device_private_key as string);

                    if (error) {
                        Sentry.captureException(error)
                        presentErrorAlert({ data: t('application.uncaught.ERROR') })
                        return
                    }

                    stellarTx = transaction

                    if (stellarTx) {

                        const uri = createSep7URI(stellarTx);

                        if (!uri) {
                            presentErrorAlert({ data: 'No valid uri creation' })
                            return

                        }

                        else {

                            let params: DelegateSigningParams = {
                                uri: uri,
                                account: walletInfo.account_key as string,
                                asset_code: props.asset.display_code
                            }

                            try {

                                presentLoader({
                                    // message: t('claimable.balance.WAITING-MESSAGE'),
                                })

                                const response = await delegateSigning(params).unwrap();
                                const stellarTxId = response.stellar_transaction_id as string;

                                // now we try to get the status of the transaction from the api

                                const result = await dispatch(ebioroApi.endpoints.getTransaction.initiate(stellarTxId, { forceRefetch: true })).unwrap();

                                let transactionStatus = result.status;

                                while (!(transactionStatus == 'completed')) {

                                    await sleep(4000); // calling the api again to check status of transaction

                                    try {

                                        const result = await dispatch(ebioroApi.endpoints.getTransaction.initiate(stellarTxId, { forceRefetch: true })).unwrap();
                                        transactionStatus = result.status

                                    } catch (error) {

                                        throw error
                                    }

                                }

                            } catch (error) {
                                presentErrorAlert({ data: t('application.uncaught.ERROR') })
                                return

                            }
                        }

                    }
                }

                if (claimable_balance_id && claimablePayment.token.type !== 'native') {

                    const assetAndIssuer = props.asset.identifier.split(':')
                    const assetCode = assetAndIssuer[0];
                    const assetIssuer = assetAndIssuer[1];

                    const [trustError, isAssetTrusted] = await stellarApi.checkAssetTrust(walletInfo.account_key as string, assetCode, assetIssuer);

                    if (trustError) {
                        presentErrorAlert({ data: JSON.stringify(trustError) })
                        return
                    }

                    // asset not trusted. Creating trust and claim transaction
                    if (!isAssetTrusted) {

                        const [error, transaction] = await txBuilder.trustAndClaim(assetCode, assetIssuer, walletInfo.account_key as string, walletInfo.device_private_key as string, claimable_balance_id);

                        if (error) {
                            presentErrorAlert({ data: JSON.stringify(error) })
                            return
                        }

                        stellarTx = transaction

                        if (stellarTx) {

                            const uri = createSep7URI(stellarTx);

                            if (!uri) {

                                presentErrorAlert({ data: 'No valid uri creation' })
                                return

                            }

                            else {

                                let params: DelegateSigningParams = {
                                    uri: uri,
                                    account: walletInfo.account_key as string,
                                    asset_code: props.asset.display_code,
                                    asset_issuer: props.asset.identifier.split(':')[1]
                                }

                                try {

                                    presentLoader({
                                        // message: t('claimable.balance.WAITING-MESSAGE'),
                                    })

                                    const response = await delegateSigning(params).unwrap()

                                    const stellarTxId = response.stellar_transaction_id as string;

                                    // now we try to get the status of the transaction from the api

                                    const result = await dispatch(ebioroApi.endpoints.getTransaction.initiate(stellarTxId, { forceRefetch: true })).unwrap();

                                    let transactionStatus = result.status;

                                    while (!(transactionStatus == 'completed')) {

                                        await sleep(4000); // calling the api again to check status of transaction

                                        try {

                                            const result = await dispatch(ebioroApi.endpoints.getTransaction.initiate(stellarTxId, { forceRefetch: true })).unwrap();
                                            transactionStatus = result.status

                                        } catch (error) {

                                            throw error
                                        }

                                    }

                                } catch (error) {
                                    presentErrorAlert({ data: t('application.uncaught.ERROR') });
                                }

                            }
                        }

                    }

                    // asset is trusted. Just claim the balance
                    else {

                        const [error, transaction] = await txBuilder.claimBalance(claimable_balance_id, walletInfo.account_key as string, walletInfo.device_private_key as string)

                        if (error) {
                            presentErrorAlert({ data: t('application.uncaught.ERROR') });
                            return
                        }

                        stellarTx = transaction

                        if (stellarTx) {

                            const uri = createSep7URI(stellarTx);

                            if (!uri) {

                                presentErrorAlert({ data: 'No valid uri creation' })
                                return

                            }

                            else {

                                let params: DelegateSigningParams = {
                                    uri: uri,
                                    account: walletInfo.account_key as string,
                                    asset_code: props.asset.display_code,
                                    asset_issuer: props.asset.identifier.split(':')[1]
                                }

                                try {

                                    presentLoader({
                                        // message: t('claimable.balance.WAITING-MESSAGE'),
                                    })

                                    const response = await delegateSigning(params).unwrap();
                                    const stellarTxId = response.stellar_transaction_id as string;

                                    // now we try to get the status of the transaction from the api

                                    const result = await dispatch(ebioroApi.endpoints.getTransaction.initiate(stellarTxId, { forceRefetch: true })).unwrap();

                                    let transactionStatus = result.status;

                                    while (!(transactionStatus == 'completed')) {

                                        await sleep(4000); // calling the api again to check status of transaction

                                        try {

                                            const result = await dispatch(ebioroApi.endpoints.getTransaction.initiate(stellarTxId, { forceRefetch: true })).unwrap();
                                            transactionStatus = result.status

                                        } catch (error) {

                                            throw error
                                        }

                                    }

                                } catch (error) {
                                    presentErrorAlert({ data: t('application.uncaught.ERROR') });
                                    return

                                }

                            }

                        }

                    }

                }


                goToClaimableBalanceSuccess()

            }

        } catch (error) {

            dismissLoader()
            presentErrorAlert(t('application.uncaught.ERROR'));
            return

        }




    }


    const createSep7URI = (stellarTx: stellarSdk.Transaction<stellarSdk.Memo<stellarSdk.MemoType>, stellarSdk.Operation[]>) => {

        if (walletInfo) {

            return sep7.createSep7URI(stellarTx, walletInfo.account_key as string);

        }

        return null

    }

    const sleep = (ms: number) => {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    useEffect(() => {

        const init = async () => {

            setAssetCode(props.operation.token.code);

            if (props.operation.claimable_balance_id) {

                const [error, response] = await stellarApi.isClaimaBalance(props.operation.claimable_balance_id);

                if (error) {
                    presentErrorAlert(JSON.stringify(error))
                    return
                }

                if (!response) {
                    setClaimSupported(false);
                    setClaimExist(false);
                    return
                }

                if (!props.operation.claim_abs_before && !props.operation.claim_not_abs_before && !props.operation.claim_unconditional && !props.operation.claim_rel_before) {

                    setClaimSupported(false);
                    return

                }

                if (
                    (props.operation.claim_abs_before && moment(props.operation.claim_abs_before).isBefore(now))
                    || (props.operation.claim_not_abs_before && moment(props.operation.claim_not_abs_before).isBefore(now))
                    || (props.operation.claim_rel_before && moment(props.operation.claim_rel_before).isBefore(now))
                ) {
                    setClaimSupported(false)
                    return
                }

            }
        }

        init()

    }, []);

    return (

        <>

            <IonHeader className="ion-no-border">
                <IonToolbar >
                    <IonButtons slot="start">
                        <IonButton onClick={() => { props.closeAction() }}>
                            <IonIcon slot="icon-only" icon={closeOutline} color="danger" />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent>

                <IonModal
                    isOpen={showClaimableBalanceSuccessPage}
                    // swipeToClose={true}
                    onDidDismiss={() => { closeClaimableBalanceSuccess() }}
                >
                    <ClaimableBalanceSuccessPage
                        closeAction={closeClaimableBalanceSuccess}

                    />

                </IonModal>

                <AppHeader
                    title={(props.operation.isRecipient ? '+' : '-') + `${parseFloat(props.operation.amount.toString()).toFixed(props.asset.decimal_places)} ${props.operation.token.code}`}
                    color={props.operation.isRecipient ? 'medium' : 'danger'}
                >

                </AppHeader>
                <IonItem lines='none'>
                    <IonLabel slot='start'>
                        <IonText className="ion-text-wrap">
                            <h5>
                                {
                                    props.operation.isRecipient ?
                                        `${t('claimable.balance.RECEIVED')}`
                                        :
                                        `${t('claimable.balance.SENT')}`
                                }

                            </h5>
                        </IonText>
                        <IonNote className="claimableBalanceDetailsPage_IonNote">
                            {moment.unix(props.operation.timestamp).format("D MMM YYYY,h:mm")}
                        </IonNote>
                    </IonLabel>
                    <IonThumbnail className="claimableBalanceDetailsPage_Thumbnail" slot="end">
                        <img src={props.asset.icon} />
                    </IonThumbnail>
                </IonItem>

                <IonCard>

                    <IonItem lines='none' >
                        <IonNote slot='start' className='claimableBalanceDetailsPage_IonNote'>
                            {t('claimable.balance.STATUS')}
                        </IonNote>
                        {
                            claimSupported ?
                                <IonNote slot='end' className='claimableBalanceDetailsPage_IonNote' color='dark'>
                                    {t('claimable.balance.STATUS-LABEL-PENDING')}
                                </IonNote>
                                :
                                <IonNote slot='end' className='claimableBalanceDetailsPage_IonNote' color='dark'>
                                    {t('claimable.balance.ALREADY_CLAIMED')}
                                </IonNote>

                        }

                    </IonItem>

                    <IonItem lines='none' >
                        {
                            props.operation.isRecipient ?
                                <IonNote slot='start' className='claimableBalanceDetailsPage_IonNote'>
                                    {t('claimable.balance.FROM')}
                                </IonNote>
                                :

                                <IonNote slot='start' className='claimableBalanceDetailsPage_IonNote'>
                                    {t('claimable.balance.TO')}
                                </IonNote>
                        }

                        <IonNote slot='end' className='claimableBalanceDetailsPage_IonNote' color='dark'>
                            {stellarUtils.shortenAccountId(props.operation.otherAccount.publicKey)}
                        </IonNote>
                    </IonItem>

                    <IonItem lines='none' >
                        <IonNote slot='start' className='claimableBalanceDetailsPage_IonNote'>
                            {t('claimable.balance.BALANCE-ID')}
                        </IonNote>
                        <IonNote slot='end' className='claimableBalanceDetailsPage_IonNote' color='dark'>
                            {stellarUtils.shortenAccountId(props.operation.claimable_balance_id ? props.operation.claimable_balance_id : '')}
                        </IonNote>
                    </IonItem>
                </IonCard>
            </IonContent>
            <div id='pageFooter'>
                <IonToolbar>
                    <IonButton
                        type="submit"
                        onClick={() => { claimBalance(props.operation) }}
                        hidden={!claimSupported}
                        expand="block"
                        id="pageFooterButton">
                        {isSpinner ? (<IonSpinner className="BUTTON-SPINNER"></IonSpinner>)
                            : t('claimable.balance.BTN')
                        }

                    </IonButton>
                </IonToolbar>
            </div>

            {/* <IonHeader className="ion-no-border">
           <IonToolbar >
                    <IonButtons slot="start">
                        <IonButton onClick={() => {props.closeAction()}}>
                            <IonIcon slot="icon-only" icon={closeOutline} color="danger" />
                        </IonButton>
                    </IonButtons> 
                    <IonTitle >
                        {  props.claimablePayment.isRecipient ?
                            
                            <b>{t('claimable.balance.TITLE-FROM')}</b>                   
                            : 
                            <b> {t('claimable.balance.TITLE-TO')}</b>
                                        
                        }
                    </IonTitle>
                </IonToolbar>
                  
            </IonHeader>

           <IonContent>

            <div className="ion-text-center">
            
                  { 
                  
                        <IonIcon icon={walletOutline} className="claimablePage_Icon"  color="primary" />
                        
                    }
             </div> 

                 <IonItem lines="none" >

                    { 
                    
                    <IonLabel className="ion-text-wrap">

                        <IonText className="ion-text-center" >

                        <h4 style={{marginTop:"5%",marginRight:"10%",marginLeft:"10%"}}>
                             {props.claimablePayment.otherAccount.publicKey}
                        </h4>

                        </IonText>

                                    
                     </IonLabel>        
                    }

                </IonItem>        

                <IonItem lines="none" className="claimablePage_Amount">

                    <IonLabel className="ion-text-wrap">

                        <IonText className="ion-text-center" color="medium">

                            <h1>
                                <NumberFormat value = {props.claimablePayment.amount.toString()} prefix= {props.claimablePayment.isRecipient?"+":"-"} displayType={'text'} thousandSeparator={true} decimalSeparator="." decimalScale ={props.asset.decimal_places} fixedDecimalScale = {true}/>
                                {" "} { assetCode}
                            </h1>

                        </IonText>
             
                    </IonLabel> 

                </IonItem>  
          
                <IonCard style={{marginTop:"10%"}}>
                    <IonGrid>
                        <IonRow>
                            <IonCol>
                                <IonItem lines="none">
                                    <IonLabel>
                                        <IonText>
                                            <h3>
                                                {t('claimable.balance.BALANCE-ID')}
                                            </h3>
                                        </IonText>
                                    </IonLabel>
                                    <IonLabel className="ion-text-wrap">
                                        <IonText>
                                            <h3 >
                                            {props.claimablePayment.claimable_balance_id}
                                             </h3>
                                        </IonText>
                                    </IonLabel>
                                </IonItem>
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol>
                                <IonItem lines="none">
                                    <IonLabel>
                                        <IonText>
                                            <h3>
                                                {t('claimable.balance.INFO')}
                                            </h3>
                                        </IonText>
                                    </IonLabel>
                                    
                                    <IonLabel className="ion-text-wrap">
                                    {
                                                // if already claimed

                                                !claimExist?
                                                
                                                <IonText className="ion-text-wrap">
                                                    <h5>    
                                                        {t('claimable.balance.ALREADY_CLAIMED')}
                                                    </h5>
                                                 </IonText>
                                                 
                                                 :

                                                // in case abs_present
                                                 props.claimablePayment.claim_abs_before?
                                                    <IonText className="ion-text-wrap">
                                                        {
                                                            moment(props.claimablePayment.claim_abs_before).isBefore(now)?
                                                            <h5>
                                                                {t('claimable.balance.CLAIM-EXPIRED')} {props.claimablePayment.claim_abs_before}
                                                            </h5>
                                                            :
                                                            <h5>    
                                                                {t('claimable.balance.CLAIM-BEFORE')} {props.claimablePayment.claim_abs_before}
                                                            </h5> 
                                                        }
                                                         
                                                    </IonText>
                                                :
                                                // in case not_abs_before is present
                                                props.claimablePayment.claim_not_abs_before?
                                                <IonText className="ion-text-wrap">
                                                    {
                                                         moment(props.claimablePayment.claim_not_abs_before).isBefore(now)?
                                                         <h5>
                                                            {t('claimable.balance.CLAIM-EXPIRED')} {props.claimablePayment.claim_not_abs_before}
                                                         </h5>
                                                         :
                                                         <h5>    
                                                        {t('claimable.balance.CLAIM-AFTER')} {props.claimablePayment.claim_not_abs_before}
                                                    </h5> 
                                                    }
                                                     
                                                </IonText>
                                                :
                                                // in case rel_abs_before is present
                                                props.claimablePayment.claim_rel_before?
                                                <IonText className="ion-text-wrap">
                                                    {
                                                        moment(props.claimablePayment.claim_rel_before).isBefore(now)?
                                                        <h5>
                                                            {t('claimable.balance.CLAIM-EXPIRED')} {props.claimablePayment.claim_rel_before}
                                                        </h5>
                                                        :
                                                        <h5>    
                                                        {t('claimable.balance.CLAIM-RELATIVE-BEFORE')} {props.claimablePayment.claim_rel_before}
                                                    </h5>  
                                                    }
                                                    
                                                </IonText>
                                                :
                                                // in case unconditional is present
                                                props.claimablePayment.claim_unconditional?
                                                
                                                <IonText className="ion-text-wrap">
                                                    <h5>    
                                                        {t('claimable.balance.CLAIM-READY')}
                                                    </h5>  
                                                </IonText>
                                                :
                                                // other types not supported yet
                                                
                                                <IonText className="ion-text-wrap">
                                                    <h5>    
                                                        {t('claimable.balance.CLAIM-NOT-SUPPORTED')}
                                                    </h5>
                                                 </IonText>

                                            }
                                        
                                    </IonLabel>
                                </IonItem>
                            </IonCol>
                        </IonRow>
                    </IonGrid>
                </IonCard>    
           </IonContent>

           <IonFooter className="ion-no-border">
                <IonToolbar>
                    <IonButton 
                        type="submit" 
                        onClick={() => {claimBalance(props.claimablePayment)}} 
                        hidden= {!claimSupported}
                        expand="block" 
                        id='pageFooterButton'>
                        >
                       {isSpinner?(<IonSpinner className="BUTTON-SPINNER"></IonSpinner>)
                       :t('claimable.balance.BTN')}       
                    </IonButton>
                </IonToolbar>
            </IonFooter> */}

        </>

    )
}

export default ClaimableBalancePage